import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { CONSTANT_APIS } from '@app/shared/constant';

export interface FeatureAvailability {
  featureName: string;
  enabled: boolean;
}

/**
 * Reusable feature-flag service. Fetches flags from the backend (e.g. by store email)
 * and caches them for the session. Use for conditional UI (e.g. biller tab).
 */
@Injectable({
  providedIn: 'root'
})
export class FeatureFlagService {

  private readonly cache = new BehaviorSubject<Map<string, boolean>>(new Map());
  private fetchDone = false;
  private inFlight: Observable<FeatureAvailability[]> | null = null;

  /** Cached flags as map (featureName -> enabled). Empty until fetchFeatures() is called. */
  readonly featuresMap$ = this.cache.asObservable();

  constructor(private http: HttpClient) {}

  /**
   * Fetch feature flags from the API. Call once after login (e.g. in sidebar or app init).
   * Safe to call multiple times; only the first successful response is cached for the session.
   */
  fetchFeatures(): Observable<FeatureAvailability[]> {
    if (this.fetchDone) {
      const list: FeatureAvailability[] = [];
      this.cache.getValue().forEach((enabled, featureName) => list.push({ featureName, enabled }));
      return of(list);
    }
    if (this.inFlight) {
      return this.inFlight;
    }
    this.inFlight = this.http.get<FeatureAvailability[]>(CONSTANT_APIS.FEATURES).pipe(
      tap((list) => {
        const map = new Map<string, boolean>();
        (list || []).forEach((f) => map.set(f.featureName, f.enabled === true));
        this.cache.next(map);
        this.fetchDone = true;
      }),
      catchError((err) => {
        this.inFlight = null;
        return throwError(err);
      }),
      tap(() => {
        this.inFlight = null;
      }),
      shareReplay(1)
    );
    return this.inFlight;
  }

  /**
   * Whether a feature is enabled (from cache). Returns false if cache not yet loaded.
   */
  isFeatureEnabled(featureName: string): boolean {
    return this.cache.getValue().get(featureName) === true;
  }

  /**
   * Observable that emits true/false when the feature flag is known.
   */
  isFeatureEnabled$(featureName: string): Observable<boolean> {
    return this.featuresMap$.pipe(
      map((m) => m.get(featureName) === true)
    );
  }

  /** Reset cache (e.g. on logout). */
  clearCache(): void {
    this.cache.next(new Map());
    this.fetchDone = false;
    this.inFlight = null;
  }
}
